/**
 * PatternLegend - Sidebar panel showing detected analytics patterns
 *
 * Collapsed by default as a "Patterns (N)" badge. Expands on click to show
 * patterns grouped by type with direction indicators.
 *
 * Design decisions from Selma/Ned review:
 * - Collapsed by default to avoid information overload (Ned)
 * - Responsive: hidden lg:block when expanded, floating badge on smaller screens (Selma)
 * - Mutually exclusive with Canvas (Selma) — handled by parent
 * - No raw confidence numbers — visual indicators only (Ned)
 * - Empty state: "No patterns detected yet. Keep chatting!" (Ned)
 *
 * @see plan.md P4.2
 */

import { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, ChevronDown, ChevronRight, BarChart2, X } from 'lucide-react';
import { C } from '../design-system/primitives/constants';

// Direction color map
const DIRECTION_COLORS = {
  positive: C.elite,  // #00CC66
  negative: C.dev,    // #FF4444
  neutral: C.gold,    // #E8B800
  watch: C.watch,     // #FFB800
};

// Direction icons
const DIRECTION_ICONS = {
  positive: TrendingUp,
  negative: TrendingDown,
  neutral: Minus,
  watch: AlertTriangle,
};

// Sort functions
const SORT_FNS = {
  recency: (a, b) => new Date(b.firstMentioned || 0) - new Date(a.firstMentioned || 0),
  priority: (a, b) => {
    const order = { negative: 0, watch: 1, positive: 2, neutral: 3 };
    return (order[a.direction] ?? 3) - (order[b.direction] ?? 3);
  },
  category: (a, b) => (a.type || '').localeCompare(b.type || ''),
};

/**
 * PatternItem — Individual pattern with left border color and direction icon
 */
function PatternItem({ pattern, onPatternClick }) {
  const dirColor = DIRECTION_COLORS[pattern.direction] || C.t3;
  const DirIcon = DIRECTION_ICONS[pattern.direction] || Minus;

  return (
    <button
      data-testid="pattern-item"
      onClick={() => onPatternClick?.(pattern.sourceTurnId)}
      className="w-full text-left px-2 py-1.5 rounded transition-colors"
      style={{
        borderLeft: `3px solid ${dirColor}`,
        background: 'transparent',
        cursor: pattern.sourceTurnId ? 'pointer' : 'default',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = C.s3; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div className="flex items-center gap-1.5">
        <DirIcon
          className="w-3 h-3 flex-shrink-0"
          style={{ color: dirColor }}
          aria-label={`${pattern.direction} trend`}
        />
        <span style={{ fontSize: 12, color: C.text, lineHeight: '16px' }}>
          {pattern.label}
        </span>
      </div>
      {pattern.dataPoints && pattern.dataPoints.length > 0 && (
        <div className="mt-0.5 ml-4.5" style={{ fontSize: 10, color: C.t2 }}>
          {pattern.dataPoints.slice(0, 2).join(' · ')}
        </div>
      )}
    </button>
  );
}

/**
 * PatternGroup — Collapsible group header with pattern type
 */
function PatternGroup({ type, patterns, onPatternClick }) {
  const [expanded, setExpanded] = useState(true);
  const displayType = type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <div className="mb-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 w-full px-1 py-1 rounded transition-colors"
        style={{ color: C.t2, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
        onMouseEnter={e => { e.currentTarget.style.background = C.s3; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {displayType}
        <span style={{ color: C.t3, fontWeight: 400, marginLeft: 'auto' }}>
          {patterns.length}
        </span>
      </button>
      {expanded && (
        <div className="space-y-0.5 mt-0.5">
          {patterns.map((pattern) => (
            <PatternItem
              key={pattern.id || pattern.label}
              pattern={pattern}
              onPatternClick={onPatternClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PatternLegend — Main sidebar component
 *
 * Collapsed: shows "Patterns (N)" badge
 * Expanded: shows full sidebar with grouped patterns
 */
export default function PatternLegend({ patterns = [], onPatternClick, onClose, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [sortBy, setSortBy] = useState('recency');

  // Group patterns by type
  const grouped = useMemo(() => {
    const sorted = [...patterns].sort(SORT_FNS[sortBy] || SORT_FNS.recency);
    const groups = {};
    for (const p of sorted) {
      const type = p.type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(p);
    }
    return groups;
  }, [patterns, sortBy]);

  const patternCount = patterns.length;

  // Collapsed state — compact badge
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors"
        style={{
          background: patternCount > 0 ? C.g08 : C.s2,
          border: `1px solid ${patternCount > 0 ? C.goldDim : C.border}`,
          color: patternCount > 0 ? C.gold : C.t2,
          fontSize: 10,
          fontWeight: 500,
        }}
      >
        <BarChart2 className="w-3 h-3" />
        Patterns{patternCount > 0 ? ` (${patternCount})` : ''}
      </button>
    );
  }

  // Expanded state — full sidebar panel
  return (
    <div
      className="flex flex-col h-full"
      style={{
        width: 256,
        background: C.s1,
        borderLeft: `1px solid ${C.border}`,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
            Patterns Detected
          </div>
          <div style={{ fontSize: 10, color: C.t2, marginTop: 1 }}>
            Trends emerging from your analysis
          </div>
        </div>
        <button
          onClick={() => { setExpanded(false); onClose?.(); }}
          className="p-1 rounded transition-colors"
          style={{ color: C.t3 }}
          onMouseEnter={e => { e.currentTarget.style.color = C.text; }}
          onMouseLeave={e => { e.currentTarget.style.color = C.t3; }}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Sort controls */}
      {patternCount > 0 && (
        <div className="flex items-center gap-1 px-3 py-1.5" style={{ borderBottom: `1px solid ${C.borderSubtle}` }}>
          {['recency', 'priority', 'category'].map((s) => (
            <button
              key={s}
              onClick={() => setSortBy(s)}
              className="px-1.5 py-0.5 rounded text-[9px] uppercase transition-colors"
              style={sortBy === s
                ? { background: C.g08, color: C.gold, fontWeight: 600 }
                : { color: C.t3 }
              }
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Pattern list */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {patternCount === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <BarChart2 className="w-6 h-6 mb-2" style={{ color: C.t3 }} />
            <p style={{ fontSize: 12, color: C.t2, lineHeight: '18px' }}>
              No patterns detected yet. Keep chatting!
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {Object.entries(grouped).map(([type, pats]) => (
              <PatternGroup
                key={type}
                type={type}
                patterns={pats}
                onPatternClick={onPatternClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Direction legend */}
      {patternCount > 0 && (
        <div
          className="flex items-center justify-center gap-3 px-3 py-1.5"
          style={{ borderTop: `1px solid ${C.borderSubtle}`, fontSize: 11, color: C.t2 }}
        >
          {Object.entries(DIRECTION_COLORS).map(([dir, color]) => {
            const Icon = DIRECTION_ICONS[dir];
            return (
              <span key={dir} className="flex items-center gap-0.5">
                <Icon className="w-2.5 h-2.5" style={{ color }} aria-hidden="true" />
                {dir}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
